import React, { useState, useContext, useEffect, useCallback } from 'react';
import { firstBy } from 'thenby';

import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';

import AppContext from '../Hooks/AppContext';
import Backdrop from '@mui/material/Backdrop';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

const Printer = props => {
  /**
   * Constants
   */

  /**
   * States
   */
  const [isLoaded, setIsLoaded] = useState(false);
  const [defaultData, setDefaultData] = useState(null);
  const [data, setData] = useState(null);
  const [deps, setDeps] = useState(null);
  const [defs, setDefs] = useState(null);
  const [notification, setNotification] = useState(null);

  /**
   * Hooks
   */
  const ac = useContext(AppContext);

  /**
   * Effects
   */
  useEffect(() => {
    ac.ajax('getPrinter', {
      idPrinter: props.value.id,
    })
      .then(response => {
        if (response.status.ok) {
          if (Boolean(response.result.data.printer.id)) {
            setDefaultData(response.result.data.printer);
            setData(response.result.data.printer);
          } else {
            setDefaultData(response.result.defaultValues.printer);
            setData({
              ...response.result.data.printer,
              idBranch: props.value.idBranch,
              printer: props.value.printer,
              idPrinterType: props.value.idPrinterType,
            });
          }

          setDeps(response.result.dependencies);
          setDefs(response.result.defaultValues);

          setIsLoaded(true);
        }
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isLoaded) return;

    let message = null;

    message = message !== null ? message : data.idBranch === '' ? 'Nu ati selectat punctul de lucru' : message;
    message = message !== null ? message : data.printer === '' ? 'Nu ati completat numele imprimantei' : message;
    message = message !== null ? message : data.idPrinterType === '' ? 'Nu ati selectat tipul imprimantei' : message;
    message = message !== null ? message : data.manufacturer === '' ? 'Nu ati completat marca imprimantei' : message;
    message = message !== null ? message : data.model === '' ? 'Nu ati completat modelul imprimantei' : message;
    message = message !== null ? message : data.serialNumber === '' ? 'Nu ati completat SN-ul imprimantei' : message;
    message = message !== null ? message : data.ip === '' ? 'Nu ati completat IP-ul imprimantei' : message;
    message = message !== null ? message : data.idPrinterDriver === '' ? 'Nu ati selectat driver-ul imprimantei' : message;

    message = message !== null ? message : JSON.stringify(data) === JSON.stringify(defaultData) ? 'Nu ati efectuat nici o modificare' : null;

    setNotification(message);
  }, [isLoaded, defaultData, data]);

  /**
   * Getters
   */
  const getBranches = useCallback(() => {
    return deps.branches.sort(firstBy('branch'));
  }, [deps]);
  const getPrinterTypes = useCallback(() => {
    return deps.printerTypes.sort(firstBy('printerType'));
  }, [deps]);
  const getPrinterDrivers = useCallback(() => {
    return deps.printerDrivers.filter(printerDriver => printerDriver.idPrinterType === data.idPrinterType).sort(firstBy('printerDriver'));
  }, [data, deps]);

  /**
   * Handlers
   */
  const handleDataChange = () => evt => {
    let mods = {};
    switch (evt.target.name) {
      case 'idPrinterType':
        mods.idPrinterDriver = defs.printer.idPrinterDriver;
        break;
      default: break;
    }

    switch (evt.target.type) {
      case 'component':
        setData(prevState => ({
          ...prevState,
          ...evt.target.data,
          ...mods,
        }));
        break;
      default:
        setData(prevState => ({
          ...prevState,
          [evt.target.name]: evt.target.value,
          ...mods,
        }));
    }
  };

  const handleClose = (evt, reason) => {
    switch (reason) {
      case 'backdropClick':
        break;
      case 'escapeKeyDown':
        if (JSON.stringify(data) === JSON.stringify(defaultData)) {
          handleCancel()();
        }
        break;
      default: break;
    }
  };

  const handleCancel = () => () => props.onCancel();
  const handleConfirm = () => () => {
    ac.ajax('savePrinter', data)
      .then(response => {
        if (response.status.ok) {
          props.onConfirm(response);
        }
      });
  };

  /**
   * Renderer
   */
  if (!isLoaded) {
    return (
      <Backdrop open sx={{color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1}}>
        <CircularProgress color='inherit' />
      </Backdrop>
    );
  }

  return (
    <Dialog maxWidth='xs' open keepMounted scroll='paper'
      disableEscapeKeyDown={ac.isLoading}
      onClose={handleClose}
    >
      {/**
       * Title
      */}
      <DialogTitle>Imprimanta: {defaultData.id === '' ? 'Adaugare' : 'Modificare'}</DialogTitle>

      {/**
       * Content
      */}
      <DialogContent>
        <DialogContentText>
          <Grid container spacing={1}>
            <Grid item xs={3}>
              <TextField disabled
                label='ID'
                placeholder='ID'
                name='id'
                value={data.id}
                onChange={handleDataChange()}
              />
            </Grid>
            <Grid item xs={9}>
              <TextField select autoFocus
                disabled={ac.isLoading}
                label='Punct de lucru'
                placeholder='Punct de lucru'
                name='idBranch'
                value={data.idBranch}
                onChange={handleDataChange()}
              >
                {getBranches().map(item => (
                  <MenuItem key={item.id} value={item.id}><Typography variant='body1'>{item.branch}</Typography></MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField required
                disabled={ac.isLoading}
                label='Imprimanta'
                placeholder='Imprimanta'
                name='printer'
                value={data.printer}
                onChange={handleDataChange()}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField select
                disabled={ac.isLoading}
                label='Tip imprimanta'
                placeholder='Tip imprimanta'
                name='idPrinterType'
                value={data.idPrinterType}
                onChange={handleDataChange()}
              >
                {getPrinterTypes().map(item => (
                  <MenuItem key={item.id} value={item.id}><Typography variant='body1'>{item.printerType}</Typography></MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField required
                disabled={ac.isLoading}
                label='Marca'
                placeholder='Marca'
                name='manufacturer'
                value={data.manufacturer}
                onChange={handleDataChange()}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField required
                disabled={ac.isLoading}
                label='Model'
                placeholder='Model'
                name='model'
                value={data.model}
                onChange={handleDataChange()}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField required
                disabled={ac.isLoading}
                label='SN (serial number)'
                placeholder='SN (serial number)'
                name='serialNumber'
                value={data.serialNumber}
                onChange={handleDataChange()}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField required
                disabled={ac.isLoading}
                label='IP'
                placeholder='IP'
                name='ip'
                value={data.ip}
                onChange={handleDataChange()}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField select
                disabled={ac.isLoading}
                label='Driver'
                placeholder='Driver'
                name='idPrinterDriver'
                value={data.idPrinterDriver}
                onChange={handleDataChange()}
              >
                {getPrinterDrivers().map(item => (
                  <MenuItem key={item.id} value={item.id}><Typography variant='body1'>{item.printerDriver}</Typography></MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField multiline rows={3}
                disabled={ac.isLoading}
                label='Observatii'
                placeholder='Observatii'
                name='description'
                value={data.description}
                onChange={handleDataChange()}
              />
            </Grid>
          </Grid>
        </DialogContentText>
      </DialogContent>

      <DialogContent>
        <DialogContentText color='error' sx={{visibility: Boolean(notification) ? 'initial' : 'hidden'}}>
          {Boolean(notification) ? notification : 'Totul pare OK!'}
        </DialogContentText>
      </DialogContent>

      {/**
       * Actions
      */}
      <DialogActions>
        <Button disabled={ac.isLoading || Boolean(notification)} onClick={handleConfirm()}>Salveaza</Button>
        <Button variant='text' color='inherit' disabled={ac.isLoading} onClick={handleCancel()}>Renunta</Button>
      </DialogActions>
    </Dialog>
  );
};

export default Printer;